import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Database, 
  Save, 
  RefreshCw, 
  TestTube, 
  CheckCircle, 
  AlertCircle,
  Server,
  Clock,
  Settings,
  Activity,
  Zap,
  Shield,
  HardDrive,
  Monitor,
  Trash2,
  Play,
  Pause
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const CacheManagement = () => {
  const [activeTab, setActiveTab] = useState('redis');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cacheConfig, setCacheConfig] = useState({});
  const [cacheStats, setCacheStats] = useState({});
  const [templateCache, setTemplateCache] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadCacheData();
  }, []);

  const loadCacheData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, templateRes] = await Promise.all([
        MockDataService.getCacheConfiguration(),
        MockDataService.getCacheStatistics(),
        MockDataService.getTemplateCacheSettings()
      ]);
      
      setCacheConfig(configRes.data);
      setCacheStats(statsRes.data);
      setTemplateCache(templateRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load cache data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving cache settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('cacheConfiguration', JSON.stringify(cacheConfig));
      localStorage.setItem('templateCacheSettings', JSON.stringify(templateCache));
      
      setMessage({ type: 'success', text: 'Cache settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save cache settings' });
    } finally {
      setSaving(false);
    }
  };

  const clearCache = async (cacheType) => {
    try {
      // Simulate cache clearing
      await new Promise(resolve => setTimeout(resolve, 2000));
      setMessage({ type: 'success', text: `${cacheType} cache cleared successfully` });
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to clear ${cacheType} cache` });
    }
  };

  const testRedisConnection = async () => {
    try {
      // Simulate Redis connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      setMessage({ type: 'success', text: 'Redis connection test successful' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Redis connection test failed' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cache Management</h1>
          <p className="text-gray-600 mt-2">Manage system cache for optimal performance</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadCacheData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('redis')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'redis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="h-4 w-4 inline mr-2" />
              Redis Configuration
            </button>
            <button
              onClick={() => setActiveTab('operations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'operations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Trash2 className="h-4 w-4 inline mr-2" />
              Cache Operations
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Template Cache
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'statistics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="h-4 w-4 inline mr-2" />
              Statistics
            </button>
          </nav>
        </div>

        {activeTab === 'redis' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Redis Cache Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableRedis">Enable Redis Cache</Label>
                      <p className="text-sm text-gray-600">Use Redis for high-performance caching</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableRedis"
                      checked={cacheConfig.enabled || false}
                      onChange={(e) => setCacheConfig({...cacheConfig, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="redisHost">Redis Host</Label>
                      <Input
                        id="redisHost"
                        value={cacheConfig.host || 'localhost'}
                        onChange={(e) => setCacheConfig({...cacheConfig, host: e.target.value})}
                        placeholder="localhost"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="redisPort">Redis Port</Label>
                      <Input
                        id="redisPort"
                        type="number"
                        value={cacheConfig.port || 6379}
                        onChange={(e) => setCacheConfig({...cacheConfig, port: parseInt(e.target.value)})}
                        min="1"
                        max="65535"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="redisPassword">Redis Password</Label>
                    <Input
                      id="redisPassword"
                      type="password"
                      value={cacheConfig.password || ''}
                      onChange={(e) => setCacheConfig({...cacheConfig, password: e.target.value})}
                      placeholder="Enter Redis password"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="redisDatabase">Database Number</Label>
                      <Input
                        id="redisDatabase"
                        type="number"
                        value={cacheConfig.database || 0}
                        onChange={(e) => setCacheConfig({...cacheConfig, database: parseInt(e.target.value)})}
                        min="0"
                        max="15"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="redisTimeout">Connection Timeout (ms)</Label>
                      <Input
                        id="redisTimeout"
                        type="number"
                        value={cacheConfig.timeout || 5000}
                        onChange={(e) => setCacheConfig({...cacheConfig, timeout: parseInt(e.target.value)})}
                        min="1000"
                        max="30000"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="defaultTTL">Default TTL (seconds)</Label>
                      <Input
                        id="defaultTTL"
                        type="number"
                        value={cacheConfig.defaultTTL || 3600}
                        onChange={(e) => setCacheConfig({...cacheConfig, defaultTTL: parseInt(e.target.value)})}
                        min="60"
                        max="86400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxMemory">Max Memory (MB)</Label>
                      <Input
                        id="maxMemory"
                        type="number"
                        value={cacheConfig.maxMemory || 512}
                        onChange={(e) => setCacheConfig({...cacheConfig, maxMemory: parseInt(e.target.value)})}
                        min="64"
                        max="8192"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Connection Test</h3>
                      <p className="text-sm text-gray-600">Test Redis connection and configuration</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={testRedisConnection}
                      className="flex items-center gap-2"
                    >
                      <TestTube className="h-4 w-4" />
                      Test Connection
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'operations' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Cache Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Clear Cache</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Application Cache</h4>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {cacheStats.appCacheSize || '2.3 MB'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Clear application-level cache</p>
                      <Button
                        variant="outline"
                        onClick={() => clearCache('Application')}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear Application Cache
                      </Button>
                    </div>
                    
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Template Cache</h4>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {cacheStats.templateCacheSize || '1.1 MB'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Clear compiled template cache</p>
                      <Button
                        variant="outline"
                        onClick={() => clearCache('Template')}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear Template Cache
                      </Button>
                    </div>
                    
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Session Cache</h4>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {cacheStats.sessionCacheSize || '0.8 MB'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Clear user session cache</p>
                      <Button
                        variant="outline"
                        onClick={() => clearCache('Session')}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear Session Cache
                      </Button>
                    </div>
                    
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Query Cache</h4>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {cacheStats.queryCacheSize || '3.2 MB'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Clear database query cache</p>
                      <Button
                        variant="outline"
                        onClick={() => clearCache('Query')}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear Query Cache
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold">Clear All Cache</h3>
                        <p className="text-sm text-gray-600">Clear all cache types at once</p>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={() => clearCache('All')}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Clear All Cache
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Template Cache Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableTemplateCache">Enable Template Cache</Label>
                      <p className="text-sm text-gray-600">Cache compiled templates for better performance</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableTemplateCache"
                      checked={templateCache.enabled || false}
                      onChange={(e) => setTemplateCache({...templateCache, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="templateTTL">Template TTL (seconds)</Label>
                      <Input
                        id="templateTTL"
                        type="number"
                        value={templateCache.ttl || 7200}
                        onChange={(e) => setTemplateCache({...templateCache, ttl: parseInt(e.target.value)})}
                        min="300"
                        max="86400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxTemplates">Max Cached Templates</Label>
                      <Input
                        id="maxTemplates"
                        type="number"
                        value={templateCache.maxTemplates || 1000}
                        onChange={(e) => setTemplateCache({...templateCache, maxTemplates: parseInt(e.target.value)})}
                        min="100"
                        max="10000"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cacheStrategy">Cache Strategy</Label>
                    <Select
                      value={templateCache.strategy || 'lru'}
                      onValueChange={(value) => setTemplateCache({...templateCache, strategy: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lru">LRU (Least Recently Used)</SelectItem>
                        <SelectItem value="lfu">LFU (Least Frequently Used)</SelectItem>
                        <SelectItem value="fifo">FIFO (First In, First Out)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="autoCompile">Auto Compile Templates</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="autoCompile"
                        checked={templateCache.autoCompile || false}
                        onChange={(e) => setTemplateCache({...templateCache, autoCompile: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Automatically compile templates on first use</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Cache Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cache Performance</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Hit Rate</span>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {cacheStats.hitRate || '94.2%'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Miss Rate</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {cacheStats.missRate || '5.8%'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Total Requests</span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {cacheStats.totalRequests || '1,234,567'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Memory Usage</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Used Memory</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {cacheStats.usedMemory || '7.2 MB'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Available Memory</span>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {cacheStats.availableMemory || '504.8 MB'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Memory Usage</span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {cacheStats.memoryUsage || '1.4%'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold mb-4">Cache Status</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Redis Status</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Template Cache</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Session Cache</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Query Cache</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CacheManagement;
